import { Column } from '@ant-design/charts'
import { ColumnConfig } from '@ant-design/plots/es/components/column'
import {
  Flex,
  Spin,
} from 'antd'
import React, {
  useMemo,
  useState,
} from 'react'
import useAsync from 'react-use/lib/useAsync'

import { EarningType } from '@/constants/earning-type'
import { FeatureKey } from '@/constants/feature-key'
import { TrendsEarning } from '@/model/analytics'
import { readCreatorOverviewTrends } from '@/redux/creator-reports'
import {
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import { ReportsPageProps } from '../../hooks/use-report-search'
import ChartEmptyPlaceholder from '../common/chart-empty-placeholder'
import EarningGrowthTooltip from '../common/earning-growth-tooltip'
import SectionTitle from '../section-title'

export default function CreatorOverviewTrends({
  searchValues,
}: ReportsPageProps) {
  const { earningType = EarningType.GrossEarnings } = searchValues
  const { creatorOverviewTrends } = useAppSelector(state => state.creatorReports)
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)

  const { startAt, endAt, period, creatorIds } = searchValues

  useAsync(async () => {
    setLoading(true)
    await dispatch(readCreatorOverviewTrends({
      startAt,
      endAt,
      period,
      creatorIds,
      permissionKey: FeatureKey.AnalyticsCreatorOverview,
    }))
    setLoading(false)
  }, [dispatch, startAt, endAt, creatorIds, period])

  const config = useMemo(() => {
    const data = creatorOverviewTrends?.[earningType] || []
    const config: ColumnConfig = {
      data,
      xField: 'dateStr',
      yField: 'earnings',
      axis: {
        x: {
          labelAutoRotate: false,
        },
      },
      height: 320,
      style: {
        maxWidth: 100,
        minHeight: 1,
        fill: '#3A5C40',
        fillOpacity: 1,
      },
      tooltip(data: TrendsEarning) {
        return { origin: data }
      },
      interaction: {
        tooltip: {
          render: (_: object, options: { title: string, items: { origin: TrendsEarning }[] }) => {
            return <div data-type="react">
              <EarningGrowthTooltip {...(options.items[0].origin)} />
            </div>
          },
        },
      },
    }

    return config
  }, [creatorOverviewTrends, earningType])

  return (
    <Spin spinning={loading}>
      <Flex vertical>
        <SectionTitle
          title="Earnings trends"
          tooltip="Sales growth and trends of all earnings from your managed Creators"
        />
        {creatorOverviewTrends?.[earningType] === undefined ?
          <ChartEmptyPlaceholder /> :
          <Column {...config} />
        }
      </Flex>
    </Spin>
  )
}
